import {
  Button,
  Card,
  Form,
  FormProps,
  Input,
  Modal,
  Typography,
  Upload,
  DatePicker,
  Popconfirm,
  Space,
} from "antd";
import {
  Home,
  Mail,
  Phone,
  Shield,
  Upload as UploadIcon,
  User,
  Edit,
  Trash2,
} from "lucide-react";
import React, { useState, useEffect, useMemo } from "react";
import { useAuth } from "~/store";
import { useQueryClient } from "@tanstack/react-query";
import { AUTH_KEYS } from "~/services/auth";
import { useNotification } from "~/hooks";
import { TFile, TStaff } from "~/types";
import { useUploadImages } from "~/services/upload";
import { RcFile } from "antd/es/upload";
import { useUpdateStaff } from "~/services/operation";
import {
  CreateFileDto,
  useCreateFile,
  useGetFiles,
  useUpdateFile,
  useDeleteFile,
  FILE_KEYS,
} from "~/services/file";
import { DocumentType } from "~/utils/constants";
import dayjs from "dayjs";

const { Title, Text } = Typography;

interface ProfileTabProps {}

interface DocumentPreviewProps {
  doc: any;
}

const DocumentPreview: React.FC<DocumentPreviewProps> = ({ doc }) => {
  const fileUrl = `${doc.filePath}`;
  const mimeType = doc.mimeType || getMimeTypeFromFileName(doc.fileName);
  const [canLoad, setCanLoad] = useState<boolean | null>(null);

  const canLoadFile = async (url: string) => {
    try {
      const response = await fetch(url, { method: "GET" });
      return response.ok;
    } catch (error) {
      console.error("Error checking file:", error);
      return false;
    }
  };

  useEffect(() => {
    canLoadFile(fileUrl).then((result) => setCanLoad(result));
  }, [fileUrl]);

  if (canLoad === null) {
    return <Text>Loading...</Text>;
  }

  if (!canLoad) {
    return (
      <div style={{ textAlign: "center" }}>
        <Text>
          Unable to load the document. The file may not exist or the server is
          not running.
        </Text>
      </div>
    );
  }

  switch (mimeType) {
    case "image/jpeg":
    case "image/png":
      return (
        <img
          src={fileUrl}
          alt={doc.title}
          style={{ maxWidth: "100%", maxHeight: "70vh", objectFit: "contain" }}
          onError={() => setCanLoad(false)}
        />
      );
    case "application/pdf":
      return (
        <embed
          src={fileUrl}
          type="application/pdf"
          style={{ width: "100%", height: "70vh" }}
          onError={() => setCanLoad(false)}
        />
      );
    case "application/msword":
    case "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
    case "text/csv":
      return (
        <div style={{ textAlign: "center" }}>
          <Text>
            This file type ({mimeType.split("/")[1].toUpperCase()}) cannot be
            previewed in the browser.
          </Text>
          <Button
            type="primary"
            href={fileUrl}
            download={doc.fileName}
            style={{ marginTop: 16 }}
          >
            Download File
          </Button>
        </div>
      );
    default:
      return <Text>Unsupported file type</Text>;
  }
};

const getMimeTypeFromFileName = (fileName: string) => {
  const extension = fileName?.split(".").pop()?.toLowerCase();
  switch (extension) {
    case "jpg":
    case "jpeg":
      return "image/jpeg";
    case "png":
      return "image/png";
    case "pdf":
      return "application/pdf";
    case "doc":
      return "application/msword";
    case "docx":
      return "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
    case "csv":
      return "text/csv";
    default:
      return "application/octet-stream";
  }
};

export const Profile: React.FC<ProfileTabProps> = () => {
  const { user, tokens } = useAuth();
  const staff: TStaff | null | undefined = user?.staff;
  const [openAddContact, setOpenAddContact] = useState(false);
  const [avatarFile, setAvatarFile] = useState<RcFile | null>(null);
  const [tempAvatarUrl, setTempAvatarUrl] = useState<string | null>(null);
  const [, setOriginalAvatarUrl] = useState<string | null>(null);
  const [openUploadModal, setOpenUploadModal] = useState(false);
  const [openViewModal, setOpenViewModal] = useState(false);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState<TFile | null>(null);
  const [editingDocument, setEditingDocument] = useState<TFile | null>(null);
  const [uploadFile, setUploadFile] = useState<RcFile | null>(null);
  const [form] = Form.useForm();
  const [editForm] = Form.useForm();
  const queryClient = useQueryClient();
  const { success, error } = useNotification();
  const uploadMutation = useUploadImages();
  const updateStaffMutation = useUpdateStaff();
  const createFileMutation = useCreateFile();
  const updateFileMutation = useUpdateFile();
  const deleteFileMutation = useDeleteFile();

  // Fetch documents for this staff member
  const { data: documentsData, refetch: refetchDocuments, isLoading: documentsLoading } = useGetFiles(
    { staffId: staff?.id },
    !!staff?.id
  );
  
  // Filter to only show documents uploaded via staff profile
  // (documents with staffId but no jobTicketId, serviceTicketId, taskId, or clientId)
  const documents = useMemo(() => {
    if (!documentsData?.items || !staff?.id) return [];
    
    return documentsData.items.filter(
      (doc) => {
        // Must have matching staffId
        if (doc.staffId !== staff.id) {
          return false;
        }
        
        // Must NOT have any of these IDs (null, undefined, or empty string are OK)
        // These indicate the document is attached to something else
        if (doc.jobTicketId) return false;
        if (doc.serviceTicketId) return false;
        if (doc.taskId) return false;
        if (doc.clientId) return false;
        
        return true;
      }
    );
  }, [documentsData?.items, staff?.id]);

  useEffect(() => {
    if (staff?.images?.[0]?.url) {
      setOriginalAvatarUrl(`${staff.images[0].url}`);
    }
    return () => {
      if (tempAvatarUrl) {
        URL.revokeObjectURL(tempAvatarUrl);
      }
    };
  }, [staff, tempAvatarUrl]);

  if (!staff || !user) {
    return <div>No staff data available</div>;
  }

  const staffMember = {
    name: `${user.firstName || ""} ${user.lastName || ""}`.trim() || "",
    role: staff.staffRole,
    phone: user.phoneNumber || "",
    email: user.email || "",
    address: staff.address || "",
    emergencyContact: {
      name: staff.nameEmergency || "",
      relation: staff.relationshipEmergency || "",
      phone: staff.phoneEmergency || "",
    },
    positions: staff.positions || [],
    documents: staff.documents || [],
  };

  const handleAvatarUpload = async () => {
    if (!avatarFile || !staff) return;

    const formData = new FormData();
    formData.append("file", avatarFile);
    formData.append("name", "Avatar");
    formData.append("staffAvatarId", staff.id);

    uploadMutation.mutate(formData, {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: AUTH_KEYS.GET_ME(!!tokens),
        });
        success({
          message: "Avatar updated successfully",
        });
        setAvatarFile(null);
        setTempAvatarUrl(null);
      },
      onError: (err) => {
        error({
          message: "Failed to upload avatar",
          description: err.message,
        });
      },
    });
  };

  const handleCancelUpload = () => {
    if (tempAvatarUrl) {
      URL.revokeObjectURL(tempAvatarUrl);
    }
    setAvatarFile(null);
    setTempAvatarUrl(null);
  };

  const beforeAvatarUpload = (file: RcFile) => {
    const isImage = file.type.startsWith("image/");
    if (!isImage) {
      error({
        message: "You can only upload image files!",
      });
      return false;
    }
    setAvatarFile(file);
    const tempUrl = URL.createObjectURL(file);
    setTempAvatarUrl(tempUrl);
    return false;
  };

  const beforeDocumentUpload = (file: RcFile) => {
    const validFileTypes = [
      "application/pdf",
      "image/jpeg",
      "image/png",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      "text/csv",
    ];
    if (!validFileTypes.includes(file.type)) {
      error({
        message:
          "You can only upload PDF, JPG, JPEG, PNG, DOC, DOCX, or CSV files!",
      });
      return false;
    }
    setUploadFile(file);
    // Set default expiration date to 1 year from now
    form.setFieldsValue({
      expires: dayjs().add(1, "year"),
    });
    setOpenUploadModal(true);
    return false;
  };

  const handleUploadDocument = async (values: any) => {
    if (!uploadFile || !staff) return;

    // Set expiration date to 1 year from now if not provided
    const expirationDate = values.expires
      ? dayjs(values.expires)
      : dayjs().add(1, "year");

    const fileData: CreateFileDto = {
      title: values.title,
      staffId: staff.id,
      type: DocumentType.OTHER,
      expires: expirationDate.toISOString(),
      file: uploadFile,
      visibleToStaff: true, // Staff should be able to see their own profile documents
    };

    createFileMutation.mutate(fileData, {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: AUTH_KEYS.GET_ME(!!tokens),
        });
        // Invalidate file queries
        queryClient.invalidateQueries({
          queryKey: FILE_KEYS.lists(),
        });
        refetchDocuments();
        success({
          message: "Document uploaded successfully",
        });
        setUploadFile(null);
        setOpenUploadModal(false);
        form.resetFields();
      },
      onError: (err) => {
        error({
          message: "Failed to upload document",
          description: err.message,
        });
      },
    });
  };

  const handleCancelDocumentUpload = () => {
    setUploadFile(null);
    setOpenUploadModal(false);
    form.resetFields();
  };

  const handleViewDocument = (doc: TFile) => {
    if (!doc.filePath) {
      error({
        message: "Cannot view document",
        description: "Document URL is missing.",
      });
      return;
    }
    setSelectedDocument(doc);
    setOpenViewModal(true);
  };

  const handleCloseViewModal = () => {
    setOpenViewModal(false);
    setSelectedDocument(null);
  };

  const handleEditDocument = (doc: TFile) => {
    setEditingDocument(doc);
    editForm.setFieldsValue({
      title: doc.title,
      expires: doc.expires ? dayjs(doc.expires) : dayjs().add(1, "year"),
    });
    setOpenEditModal(true);
  };

  const handleUpdateDocument = async (values: any) => {
    if (!editingDocument) return;

    try {
      await updateFileMutation.mutateAsync({
        id: editingDocument.id,
        title: values.title,
        expires: values.expires ? dayjs(values.expires).toISOString() : undefined,
      });
      success({ message: "Document updated successfully" });
      refetchDocuments();
      queryClient.invalidateQueries({
        queryKey: AUTH_KEYS.GET_ME(!!tokens),
      });
      queryClient.invalidateQueries({
        queryKey: FILE_KEYS.lists(),
      });
      setOpenEditModal(false);
      setEditingDocument(null);
      editForm.resetFields();
    } catch (err: any) {
      error({
        message: "Failed to update document",
        description: err.message,
      });
    }
  };

  const handleDeleteDocument = async (docId: string) => {
    try {
      await deleteFileMutation.mutateAsync(docId);
      success({ message: "Document deleted successfully" });
      refetchDocuments();
      queryClient.invalidateQueries({
        queryKey: AUTH_KEYS.GET_ME(!!tokens),
      });
      queryClient.invalidateQueries({
        queryKey: FILE_KEYS.lists(),
      });
      if (selectedDocument?.id === docId) {
        setSelectedDocument(null);
        setOpenViewModal(false);
      }
    } catch (err: any) {
      error({
        message: "Failed to delete document",
        description: err.message,
      });
    }
  };

  const handleCancelEdit = () => {
    setOpenEditModal(false);
    setEditingDocument(null);
    editForm.resetFields();
  };

  return (
    <div className="space-y-3 md:space-y-4 md:space-y-4">
      <Title level={3} className="mb-0 text-lg md:text-xl ">
        My Profile
      </Title>
      <Card
        classNames={{
          body: "p-0",
        }}
        className="mb-3 md:mb-4 xl:mb-6 border-0 md:border"
      >
        <div className="p-3 md:p-4 xl:p-5 border-b">
          <Text strong className="text-sm md:text-base xl:text-lg">
            Personal Information
          </Text>
        </div>
        <div className="p-3 md:p-4 xl:p-5">
          <div className="flex flex-col md:flex-row items-start md:items-center mb-4 md:mb-6 gap-3 md:gap-4">
            <div className="w-16 h-16 md:w-20 md:h-20 xl:w-24 xl:h-24 bg-gray-200 rounded-full flex items-center justify-center flex-shrink-0">
              {tempAvatarUrl ? (
                <img
                  src={tempAvatarUrl}
                  alt="Temporary Profile"
                  className="w-full h-full rounded-full object-cover"
                />
              ) : staff?.avatar?.[0]?.url ? (
                <img
                  src={`${staff.avatar[0].url}`}
                  alt="Profile"
                  className="w-full h-full rounded-full object-cover"
                />
              ) : (
                <User size={24} className="md:size-8 xl:size-9 text-gray-400" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <Title level={4} className="text-base md:text-lg xl:text-xl mb-1">
                {staffMember.name}
              </Title>
              <Text type="secondary" className="text-sm md:text-base block mb-2">
                {staffMember.role?.name}
              </Text>
              <div className="mt-2">
                <Upload
                  beforeUpload={beforeAvatarUpload}
                  showUploadList={false}
                  accept="image/*"
                >
                  <Button type="link" size="small" className="p-0 h-auto">
                    Update Photo
                  </Button>
                </Upload>
                {avatarFile && (
                  <div className="mt-2 flex flex-col md:flex-row gap-2">
                    <Button
                      type="primary"
                      onClick={handleAvatarUpload}
                      loading={
                        uploadMutation.isPending ||
                        updateStaffMutation.isPending
                      }
                      size="small"
                    >
                      Confirm Upload
                    </Button>
                    <Button
                      onClick={handleCancelUpload}
                      disabled={
                        uploadMutation.isPending ||
                        updateStaffMutation.isPending
                      }
                      size="small"
                    >
                      Cancel
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 md:gap-6">
            <div className="space-y-3 md:space-y-4">
              <div>
                <Text type="secondary" className="text-xs md:text-sm block mb-1">
                  Phone
                </Text>
                <Input
                  prefix={<Phone size={14} className="md:size-4 text-gray-400" />}
                  value={user?.phoneNumber ?? ""}
                  readOnly
                  className="mb-3 md:mb-4"
                  size="large"
                />
              </div>
              <div>
                <Text type="secondary" className="text-xs md:text-sm block mb-1">
                  Email
                </Text>
                <Input
                  prefix={<Mail size={14} className="md:size-4 text-gray-400" />}
                  value={user?.email ?? ""}
                  readOnly
                  className="mb-3 md:mb-4"
                  size="large"
                />
              </div>
              <div>
                <Text type="secondary" className="text-xs md:text-sm block mb-1">
                  Address
                </Text>
                <Input
                  prefix={<Home size={14} className="md:size-4 text-gray-400" />}
                  value={staff?.address || ""}
                  readOnly
                  size="large"
                />
              </div>
            </div>
            <div>
              <Text type="secondary" className="text-xs md:text-sm block mb-2">
                Emergency Contact
              </Text>
              <Card className="mb-3 md:mb-4 border-0 md:border bg-gray-50">
                <div className="mb-2">
                  <Text type="secondary" className="text-xs md:text-sm">
                    Name:
                  </Text>
                  <Text strong className="text-sm md:text-base">
                    {staffMember.emergencyContact.name || "Not provided"}
                  </Text>
                </div>
                <div className="mb-2">
                  <Text type="secondary" className="text-xs md:text-sm">
                    Relationship:
                  </Text>
                  <Text className="text-sm md:text-base">
                    {staffMember.emergencyContact.relation || "Not provided"}
                  </Text>
                </div>
                <div>
                  <Text type="secondary" className="text-xs md:text-sm">
                    Phone:
                  </Text>
                  <Text className="text-sm md:text-base">
                    {staffMember.emergencyContact.phone || "Not provided"}
                  </Text>
                </div>
              </Card>
              <Button
                block
                className="flex items-center justify-center"
                onClick={() => setOpenAddContact(true)}
                size="large"
              >
                <Shield size={16} className="mr-2" /> Update Emergency Contact
              </Button>
              <UpdateEmergencyContact
                open={openAddContact}
                onClose={() => setOpenAddContact(false)}
              />
            </div>
          </div>
        </div>
      </Card>
      <Card
        classNames={{
          body: "p-0",
        }}
        className="mb-6"
      >
        <div className="p-4 border-b">
          <Text strong>Positions & Rates</Text>
        </div>
        <div className="p-4 space-y-4">
          {staffMember.positions?.length > 0 ? (
            staffMember.positions.map((position, index) => (
              <div
                key={index}
                className="flex justify-between items-center border-b pb-3"
              >
                <Text strong>{position.name}</Text>
                <Text>${position.rate}/hr</Text>
              </div>
            ))
          ) : (
            <Text type="secondary">No positions assigned</Text>
          )}
        </div>
      </Card>
      <Card
        classNames={{
          body: "p-0",
        }}
      >
        <div className="p-4 border-b">
          <Text strong>Required Documents</Text>
        </div>
        <div className="p-4 space-y-4">
          {documentsLoading && (
            <Text type="secondary">Loading documents...</Text>
          )}
          {documents.length > 0 ? (
            documents.map((doc) => (
              <div
                key={doc.id}
                className="flex justify-between items-center border-b pb-3"
              >
                <div className="flex-1">
                  <Text strong>{doc.title || "Untitled Document"}</Text>
                  <Text type="secondary" className="text-sm block">
                    Uploaded: {new Date(doc.createdAt).toLocaleDateString()} ·{" "}
                    Expires:{" "}
                    {doc.expires
                      ? new Date(doc.expires).toLocaleDateString()
                      : "N/A"}
                  </Text>
                </div>
                <Space>
                  <Button
                    type="link"
                    icon={<Edit size={14} />}
                    onClick={() => handleEditDocument(doc)}
                    size="small"
                  >
                    Edit
                  </Button>
                  <Popconfirm
                    title="Delete document"
                    description="Are you sure you want to delete this document?"
                    onConfirm={() => handleDeleteDocument(doc.id)}
                    okText="Yes"
                    cancelText="No"
                  >
                    <Button
                      type="link"
                      danger
                      icon={<Trash2 size={14} />}
                      size="small"
                    >
                      Delete
                    </Button>
                  </Popconfirm>
                  <Button
                    type="link"
                    onClick={() => handleViewDocument(doc)}
                    size="small"
                  >
                    View
                  </Button>
                </Space>
              </div>
            ))
          ) : (
            <Text type="secondary">No documents uploaded</Text>
          )}
          <Card className="border-2 border-dashed text-center">
            <UploadIcon size={24} className="mx-auto mb-2 text-gray-400" />
            <Text strong className="mb-1 block">
              Upload Document
            </Text>
            <Text type="secondary" className="text-sm mb-3 block">
              PDF, JPG, JPEG, PNG, DOC, DOCX, or CSV files accepted
            </Text>
            <Upload
              beforeUpload={beforeDocumentUpload}
              showUploadList={false}
              accept="application/pdf,image/jpeg,image/png,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/csv"
            >
              <Button type="primary">Select File</Button>
            </Upload>
          </Card>
        </div>
      </Card>

      <Modal
        title="Upload Document"
        open={openUploadModal}
        onCancel={handleCancelDocumentUpload}
        okButtonProps={{
          loading: createFileMutation.isPending,
          disabled: createFileMutation.isPending,
        }}
        onOk={() => form.submit()}
      >
        <Form form={form} onFinish={handleUploadDocument} layout="vertical">
          <Form.Item
            name="title"
            label="Title"
            rules={[
              { required: true, message: "Please enter the document title" },
            ]}
          >
            <Input placeholder="Enter document title" />
          </Form.Item>
          <Form.Item
            name="expires"
            label="Expiration Date"
            initialValue={dayjs().add(1, "year")}
            rules={[
              { required: true, message: "Please select the expiration date" },
            ]}
          >
            <DatePicker
              style={{ width: "100%" }}
              placeholder="Select expiration date"
              format="YYYY-MM-DD"
              disabledDate={(current) =>
                current && current < dayjs().startOf("day")
              }
            />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={selectedDocument?.title || "View Document"}
        open={openViewModal}
        onCancel={handleCloseViewModal}
        footer={[
          <Button
            key="download"
            type="primary"
            onClick={async () => {
              if (selectedDocument) {
                try {
                  const response = await fetch(
                    selectedDocument?.filePath || "",
                    { method: "GET" }
                  );
                  if (!response.ok) throw new Error("Failed to fetch file");
                  const blob = await response.blob();
                  const url = window.URL.createObjectURL(blob);
                  const link = document.createElement("a");
                  link.href = url;
                  link.download = selectedDocument.fileName || "document";
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
                  window.URL.revokeObjectURL(url);
                } catch (err: any) {
                  error({
                    message: "Failed to download file",
                    description: err.message,
                  });
                }
              }
            }}
          >
            Download File
          </Button>,
          <Button key="close" onClick={handleCloseViewModal}>
            Close
          </Button>,
        ]}
        width={800}
      >
        {selectedDocument && <DocumentPreview doc={selectedDocument} />}
      </Modal>

      <Modal
        title="Edit Document"
        open={openEditModal}
        onCancel={handleCancelEdit}
        footer={null}
        width={500}
      >
        <Form
          form={editForm}
          layout="vertical"
          onFinish={handleUpdateDocument}
        >
          <Form.Item
            name="title"
            label="Document Title"
            rules={[{ required: true, message: "Please enter document title" }]}
          >
            <Input placeholder="Enter document title" />
          </Form.Item>

          <Form.Item
            name="expires"
            label="Expiration Date"
            rules={[
              { required: true, message: "Please select the expiration date" },
            ]}
          >
            <DatePicker
              style={{ width: "100%" }}
              placeholder="Select expiration date"
              format="YYYY-MM-DD"
              disabledDate={(current) =>
                current && current < dayjs().startOf("day")
              }
            />
          </Form.Item>

          <Form.Item>
            <div className="flex justify-end space-x-2">
              <Button onClick={handleCancelEdit}>Cancel</Button>
              <Button
                type="primary"
                htmlType="submit"
                loading={updateFileMutation.isPending}
              >
                Save Changes
              </Button>
            </div>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

const formItemLayout = {
  labelCol: {
    xs: { span: 24 },
    sm: { span: 6 },
  },
  wrapperCol: {
    xs: { span: 24 },
    sm: { span: 18 },
  },
};

const UpdateEmergencyContact = ({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) => {
  const { user, tokens } = useAuth();
  const qc = useQueryClient();
  const [form] = Form.useForm();
  const { success, error } = useNotification();
  const updateMutation = useUpdateStaff();

  const onFinish: FormProps["onFinish"] = (values) => {
    if (!user) return;
    updateMutation.mutate(
      { staff: values, id: user.id },
      {
        onSuccess: () => {
          qc.invalidateQueries({
            queryKey: AUTH_KEYS.GET_ME(!!tokens),
          });
          success({
            message: "Emergency contact updated successfully",
          });
          onClose();
          form.resetFields();
        },
        onError: (err) => {
          error({
            message: "Failed to update emergency contact",
            description: err.message,
          });
        },
      }
    );
  };

  return (
    <Modal
      title="Add Emergency Contact"
      open={open}
      onCancel={onClose}
      okButtonProps={{
        loading: updateMutation.isPending,
        disabled: updateMutation.isPending,
      }}
      onOk={form.submit}
    >
      <Form
        {...formItemLayout}
        labelAlign="left"
        form={form}
        onFinish={onFinish}
      >
        <Form.Item name="nameEmergency" label="Name">
          <Input />
        </Form.Item>
        <Form.Item name="relationshipEmergency" label="Relationship">
          <Input />
        </Form.Item>
        <Form.Item name="phoneEmergency" label="Phone">
          <Input />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default Profile;
